// src/utils/checklistHelpers.js
// Funcoes puras para parsing e agrupamento de checklist por secoes

// Ordem natural de limpeza - EN keys (usadas internamente)
const SECTION_ORDER = [
  'START', 'ENTRANCE', 'BEDROOM', 'BEDROOMS', 'ALL ROOMS',
  'BATHROOM', 'KITCHEN', 'LIVING ROOM', 'SALA',
  'GENERAL', 'EXTERNAL', 'FINISH', 'OTHER'
];

// Mapeamento PT → EN key (para normalizar secoes PT para a mesma key)
const PT_TO_EN_KEY = {
  'INÍCIO': 'START',
  'INICIO': 'START',
  'ENTRADA': 'ENTRANCE',
  'QUARTO': 'BEDROOM',
  'QUARTOS': 'BEDROOMS',
  'TODOS OS CÔMODOS': 'ALL ROOMS',
  'BANHEIRO': 'BATHROOM',
  'COZINHA': 'KITCHEN',
  'SALA': 'LIVING ROOM',
  'GERAL': 'GENERAL',
  'EXTERNO': 'EXTERNAL',
  'FINAL': 'FINISH',
  'OUTROS': 'OTHER'
};

// Labels bilingues para exibicao
const SECTION_LABELS = {
  'START':       { en: 'Start', pt: 'Início' },
  'ENTRANCE':    { en: 'Entrance', pt: 'Entrada' },
  'BEDROOM':     { en: 'Bedroom', pt: 'Quarto' },
  'BEDROOMS':    { en: 'Bedrooms', pt: 'Quartos' },
  'ALL ROOMS':   { en: 'All Rooms', pt: 'Todos os Cômodos' },
  'BATHROOM':    { en: 'Bathroom', pt: 'Banheiro' },
  'KITCHEN':     { en: 'Kitchen', pt: 'Cozinha' },
  'LIVING ROOM': { en: 'Living Room', pt: 'Sala' },
  'GENERAL':     { en: 'General', pt: 'Geral' },
  'EXTERNAL':    { en: 'External', pt: 'Externo' },
  'FINISH':      { en: 'Finish', pt: 'Final' },
  'OTHER':       { en: 'Other', pt: 'Outros' }
};

/**
 * Extrai prefixo [SECTION] do texto de um item
 * @param {string} text - Texto do item (ex: "[BEDROOM] Replace linens")
 * @returns {{ section: string, displayText: string }}
 */
export function parseChecklistSection(text) {
  if (!text) return { section: 'OTHER', displayText: text || '' };
  const match = text.match(/^\[([^\]]+)\]\s*/);
  if (!match) return { section: 'OTHER', displayText: text };

  const rawSection = match[1].toUpperCase();
  // Normaliza PT para EN key
  const section = PT_TO_EN_KEY[rawSection] || rawSection;
  const displayText = text.slice(match[0].length);

  return { section, displayText };
}

/**
 * Agrupa checklist flat por secao, preservando originalIndex
 * @param {Array<{text: string, done: boolean}>} checklist
 * @returns {Array<{section: string, items: Array<{text: string, done: boolean, displayText: string, originalIndex: number}>}>}
 */
export function groupChecklistBySection(checklist) {
  if (!checklist || checklist.length === 0) return [];

  const groups = {};

  checklist.forEach((item, index) => {
    const itemText = typeof item === 'string' ? item : (item?.text || '');
    const { section, displayText } = parseChecklistSection(itemText);

    if (!groups[section]) {
      groups[section] = [];
    }
    groups[section].push({
      ...item,
      displayText,
      originalIndex: index
    });
  });

  // Ordena secoes pelo fluxo natural
  const sortedSections = Object.keys(groups).sort((a, b) => {
    const indexA = SECTION_ORDER.indexOf(a);
    const indexB = SECTION_ORDER.indexOf(b);
    // Secoes desconhecidas vao pro final (antes de OTHER)
    const posA = indexA === -1 ? SECTION_ORDER.length - 1 : indexA;
    const posB = indexB === -1 ? SECTION_ORDER.length - 1 : indexB;
    return posA - posB;
  });

  return sortedSections.map(section => ({
    section,
    items: groups[section]
  }));
}

/**
 * Retorna label traduzido para uma secao
 * @param {string} sectionKey - Key da secao (ex: 'BEDROOM')
 * @param {string} language - 'en' ou 'pt'
 * @returns {string}
 */
export function getSectionLabel(sectionKey, language) {
  const labels = SECTION_LABELS[sectionKey];
  if (!labels) return sectionKey;
  return labels[language] || labels.en;
}

/**
 * Calcula progresso de uma lista de itens
 * @param {Array<{done: boolean}>} items
 * @returns {{ completed: number, total: number, percentage: number, isComplete: boolean }}
 */
export function getSectionProgress(items) {
  if (!items || items.length === 0) return { completed: 0, total: 0, percentage: 0, isComplete: false };

  const total = items.length;
  const completed = items.filter(item => item.done).length;
  const percentage = Math.round((completed / total) * 100);

  return { completed, total, percentage, isComplete: completed === total };
}

/**
 * Detecta se checklist usa prefixos [SECTION]
 * @param {Array<{text: string}>} checklist
 * @returns {boolean}
 */
export function hasChecklistSections(checklist) {
  if (!checklist || checklist.length === 0) return false;
  return checklist.some(item => /^\[[^\]]+\]/.test(item.text));
}

/**
 * Lista de secoes disponiveis para dropdown de adicionar item
 * @param {string} language - 'en' ou 'pt'
 * @returns {Array<{key: string, label: string}>}
 */
export function getAvailableSections(language) {
  // Exclui OTHER (itens sem secao) e secoes raras
  const mainSections = [
    'START', 'ENTRANCE', 'BEDROOM', 'BEDROOMS', 'ALL ROOMS',
    'BATHROOM', 'KITCHEN', 'LIVING ROOM', 'GENERAL', 'EXTERNAL', 'FINISH'
  ];

  return mainSections.map(key => ({
    key,
    label: getSectionLabel(key, language)
  }));
}
